import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './context/AuthContext'
import { useAdmin } from './context/AdminContext'

// Layouts
import OnboardingLayout from './layouts/OnboardingLayout'
import AdminLayout from './layouts/AdminLayout'

// Components
import ImpersonationBanner from './components/ImpersonationBanner'
import DemoModeBanner from './components/DemoModeBanner'

// Auth pages
import LoginPage from './pages/auth/LoginPage'
import SignUpPage from './pages/auth/SignUpPage'
import CompleteProfilePage from './pages/auth/CompleteProfilePage'

// Onboarding pages
import USPReviewPage from './pages/onboarding/USPReviewPage'
import InterestPage from './pages/onboarding/InterestPage'
import ScheduleIntroPage from './pages/onboarding/ScheduleIntroPage'
import ConsentPage from './pages/onboarding/ConsentPage'
import ConsentPendingPage from './pages/onboarding/ConsentPendingPage'
import ConsentCompletePage from './pages/onboarding/ConsentCompletePage'
import APISetupPage from './pages/onboarding/APISetupPage'
import ScheduleDemoPage from './pages/onboarding/ScheduleDemoPage'
import CompletePage from './pages/onboarding/CompletePage'

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard'

// Landing page redirect - unauthenticated users go to insurevision.ai
const LandingRedirect = () => {
  const { isAuthenticated, loading } = useAuth()

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      window.location.replace('https://insurevision.ai')
    }
  }, [loading, isAuthenticated])

  if (loading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    )
  }

  return <Navigate to="/onboarding" replace />
}

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    )
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  
  return children
}

// Admin route wrapper - requires authentication AND admin status
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()
  const { isAdmin, isCheckingAdmin } = useAdmin()
  
  if (loading || isCheckingAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    )
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  
  if (!isAdmin) {
    return <Navigate to="/onboarding" replace />
  }
  
  return children
}

function App() {
  return (
    <>
      {/* Impersonation banner - always rendered, self-hides when not impersonating */}
      <ImpersonationBanner />
      {/* Demo mode banner - always rendered, self-hides when not in demo mode */}
      <DemoModeBanner />

      <Routes>
        {/* Public routes - no auth required */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        
        {/* Landing page - redirects unauthenticated to insurevision.ai */}
        <Route path="/" element={<LandingRedirect />} />
        {/* Internal fallback - USP Review page still accessible directly */}
        <Route path="/usp-review" element={<USPReviewPage />} />
        
        {/* Profile completion for Google sign-up - auth required */}
        <Route 
          path="/complete-profile" 
          element={
            <ProtectedRoute>
              <CompleteProfilePage />
            </ProtectedRoute>
          }
        />
        
        {/* Protected onboarding routes - auth required */}
        <Route 
          path="/onboarding" 
          element={
            <ProtectedRoute>
              <OnboardingLayout />
            </ProtectedRoute>
          }
        >
          {/* Default route - redirect to schedule-intro (new user start) */}
          <Route index element={<Navigate to="schedule-intro" replace />} />
          
          {/* Legacy route - keep for backwards compatibility */}
          <Route path="interest" element={<InterestPage />} />
          
          {/* Intro meeting booking (stages 02, 03) */}
          <Route path="schedule-intro" element={<ScheduleIntroPage />} />
          
          {/* Personalised demo flow (stages 03B onwards) */}
          <Route path="consent" element={<ConsentPage />} />
          <Route path="consent-pending" element={<ConsentPendingPage />} />
          <Route path="consent-complete" element={<ConsentCompletePage />} />
          <Route path="api-setup" element={<APISetupPage />} />
          <Route path="schedule-demo" element={<ScheduleDemoPage />} />
          
          {/* NEW: Provisioned/complete page (stage 11) */}
          <Route path="complete" element={<CompletePage />} />
        </Route>
        
        {/* Admin routes - requires admin status */}
        <Route 
          path="/admin" 
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
        </Route>
        
        {/* Catch all - redirect to landing page */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}

export default App
